import { NgClass } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';
import { DPValidationType } from 'src/app/core/models/enums/DPValidationType';
import { UtilsService } from 'src/app/core/services/utils.service';
import { DateComponent } from '../date/date.component';
import { FieldErrorComponent } from '../../field-error/field-error.component';

/**
 * Start/End date component.
 *
 * The value is following string (start date to end date):
 * "mm-dd-yyyy to mm-dd-yyyy"
 */
@Component({
  selector: 'app-start-end-date',
  standalone: true,
  imports: [NgClass, DateComponent, FieldErrorComponent],
  templateUrl: './start-end-date.component.html',
  styleUrls: ['./start-end-date.component.scss'],
})
export class StartEndDateComponent implements OnInit {
  @Input() field: DPFormField;
  @Input() itemClass = 'col-12 col-md-6';
  @Input() control: UntypedFormControl;
  @Input() disabled: boolean;
  @Input() markAsTouchedSubject: Subject<void>;

  // Start date field definition.
  public startField: DPFormField = {
    label: 'Start Date',
    name: 'start',
    type: DPFormFieldType.Date,
  };

  // End date field definition.
  public endField: DPFormField = {
    label: 'End Date',
    name: 'end',
    type: DPFormFieldType.Date,
  };

  // Start date control
  public startControl = new UntypedFormControl();

  // End date control
  public endControl = new UntypedFormControl();

  isFutureDateNotAllowed: boolean;

  isPastDateNotAllowed: boolean;

  private unsubscribe$ = new Subject<void>();

  constructor(private utils: UtilsService) {}

  ngOnInit(): void {
    if (this.control?.value) {
      /**
       * Populates start date and end date from control value.
       */
      const [startDate, endDate] = this.control.value.split(' to ');
      this.startControl.patchValue(startDate);
      this.endControl.patchValue(endDate);
    }

    this.isFutureDateNotAllowed = !!this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.InvalidateFutureDate
    );
    this.isPastDateNotAllowed = !!this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.InvalidatePastDate
    );

    /**
     * When start date control value changes,
     * validate and update the control value.
     */
    this.startControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        // First validate individual controls
        this.validateControl(this.startControl, 'Start date');
        this.validateControl(this.endControl, 'End date');

        // Then check start/end date relationship
        //this.compareStartAndEndDate();

        // Finally update the parent control value and propagate errors
        this.updateValue();
      });

    /**
     * When end date control value changes,
     * validate and update the control value.
     */
    this.endControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        // First validate individual controls
        this.validateControl(this.startControl, 'Start date');
        this.validateControl(this.endControl, 'End date');

        // Then check start/end date relationship
        //this.compareStartAndEndDate();

        // Finally update the parent control value and propagate errors
        this.updateValue();
      });

    // Subscribe to markAsTouched events from parent form
    if (this.markAsTouchedSubject) {
      this.markAsTouchedSubject
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => {
          console.log('markAsTouched event received in StartEndDateComponent');
          this.markAsTouched();
        });
    }

    // Don't perform initial validation here - wait for user interaction
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
  }

  /**
   * Mark controls as touched and update validation errors
   * This should be called by the parent form when trying to submit
   */
  public markAsTouched() {
    console.log('triggered ');
    this.startControl.markAsTouched();
    this.endControl.markAsTouched();

    // Perform validation on both controls directly since we're submitting
    const startDate = this.startControl.value;
    const endDate = this.endControl.value;

    // Clear previous errors
    this.startControl.setErrors(null);
    this.endControl.setErrors(null);

    // Validate required fields
    if (this.field.required) {
      if (!startDate) {
        this.startControl.setErrors({ custom: 'Please enter start date.' });
      }
      if (!endDate) {
        this.endControl.setErrors({ custom: 'Please enter end date.' });
      }
    }

    // Validate date range if both dates are provided
    if (startDate && endDate) {
      if (this.utils.compareDates(startDate, endDate) > 0) {
        const error = { custom: 'Start date must be before end date' };
        this.startControl.setErrors(error);
        this.endControl.setErrors(error);
      }
    }

    // Update parent control value and propagate errors - pass true for isSubmitting
    this.updateValue(true);

    // Ensure parent control has errors if any child control has errors
    if (this.startControl.errors || this.endControl.errors) {
      this.control.setErrors(
        this.startControl.errors || this.endControl.errors
      );
    }
  }

  /**
   * Update control value
   */
  private updateValue(isSubmitting = false) {
    if (this.control) {
      this.control.patchValue(
        `${this.startControl.value} to ${this.endControl.value}`
      );
    }

    // Clear errors on individual controls if they're valid
    if (!this.startControl.errors) {
      this.startControl.setErrors(null);
    }
    if (!this.endControl.errors) {
      this.endControl.setErrors(null);
    }

    // Only propagate errors to parent control if both fields have been touched
    // or we're in submission mode
    if (
      isSubmitting ||
      (this.startControl.touched && this.endControl.touched)
    ) {
      if (this.startControl.errors || this.endControl.errors) {
        // If either control has errors, set error on the parent control as well
        const error = this.startControl.errors || this.endControl.errors;
        this.control.setErrors(error);
      } else {
        // Both controls are valid
        this.control.setErrors(null);
      }
    }
  }

  /**
   * Validates date control
   */
  private validateControl(control: UntypedFormControl, label: string) {
    const today = this.utils.getToday();
    const val = control.value;
    let error = '';

    // Only validate if THIS SPECIFIC control has been touched (not any control in the form)
    if (control.touched) {
      if (this.field.required && !val) {
        /**
         * If the field is required, start/end date fields are also required.
         */
        error = `Please enter ${label.toLowerCase()}.`;
      } else if (
        this.isFutureDateNotAllowed &&
        this.utils.compareDates(today, val) < 0
      ) {
        /**
         * If the field doesn't future date is not allowed,
         * start/end date fields also don't allow future dates.
         */
        error = `${label} cannot be a future date.`;
      } else if (
        this.isPastDateNotAllowed &&
        this.utils.compareDates(today, val) > 0
      ) {
        /**
         * If the field doesn't past date is not allowed,
         * start/end date fields also don't allow past dates.
         */
        error = `${label} cannot be a past date.`;
      }
    }

    if (error) {
      control.setErrors({ custom: error });
      // Don't modify the parent control here - let updateValue handle that
    } else {
      control.setErrors(null);
    }

    return !error;
  }

  /**
   * Compare start & end dates.
   *
   * @returns True if start date <= end date
   */
  private compareStartAndEndDate() {
    // Only compare dates if both start and end dates have values
    if (this.startControl.value && this.endControl.value) {
      if (
        this.utils.compareDates(
          this.startControl.value,
          this.endControl.value
        ) > 0
      ) {
        const error = {
          custom: 'Start date must be before end date',
        };
        this.startControl.setErrors(error);
        this.endControl.setErrors(error);
        this.control.setErrors(error);
        this.control.markAsTouched();
        return false;
      }
    }

    // Don't clear any existing errors here - let the validateControl method handle that
    return true;
  }
}
